// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker from 'wdna-date-selector';

import { UserDO } from 'wdna-components';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableListDO, typeSensor, TypeSensorListDO } from '../../../../configuration/requests';
import { company, CompanyDO } from '../../../../management/requests';
import { sensor, SensorDO, SensorListDO } from '../../../requests';

interface Props {
  rowId?: number;
}

interface State {
  companyArray: CompanyDO[];
  typeSensorArray: TypeSensorListDO[];
  sensorArray: SensorListDO[];
  typeVariableArray: TypeSensorVariableListDO[];

  date: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;
  groupTime: string;

  multiple: boolean;
  variables: any[];
  checked: boolean;
  autoRefresh: boolean;

  id?: number;
  companyId?: number;
  typeSensorId?: number;
}

export default class Graph extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.state = {
      companyArray: [],
      typeSensorArray: [],
      sensorArray: [],
      typeVariableArray: [],

      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '60m',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',
      groupTime: '0',

      multiple: true,
      variables: [],
      checked: false,
      autoRefresh: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    if (this.props.rowId) {
      this.loadVariables().catch(() => { /* pass */ });
    } else {
      this.loadFilter().catch(() => { /* pass */ });
    }
  };

  public initialDates = (): Date[] => {
    const today: Date = moment().add(1, 'days').toDate();
    const twoDaysBefore: Date = moment().toDate();
    return [twoDaysBefore, today];
  };

  public isToday = (): boolean => {
    let a: boolean = true;
    const b: string = moment(this.state.date[1]).format('YYYY-MM-DD');
    const c: string = moment(new Date()).format('YYYY-MM-DD');
    if (b !== c) {
      a = false;
    }
    return a;
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public submit = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;
    if (!idSensor) {
      messageError('Error', 'Please select a device');
      return;
    }

    const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
    const typeSensorId: number | undefined = sensorInfo?.typeSensor.id;
    const req: Record<string, any> = {
      group: this.state.groupTime,
      values: this.state.variables,
      round: false,
    };
    if (this.state.dateIni) {
      req.from = this.state.dateIni;
      if (this.state.timeIni) req.from = `${req.from}T${this.state.timeIni}:00.000Z`;
    }
    if (this.state.dateEnd) {
      req.to = this.state.dateEnd;
      req.to = moment(req.to).add(1, 'days').format('YYYY-MM-DD');
      if (this.state.timeEnd) req.to = `${req.to}T${this.state.timeEnd}:00.000Z`;
    }

    if (typeSensorId === 4) {
      const elem: string = '4';
      if (req.values.includes(elem)) {
        const dim: number = this.state.variables.length;
        const pos: number = req.values.indexOf(elem);
        req.operators = [];
        req.operators = Array.from({ length: dim }, () => 'mean');
        req.operators[pos] = 'sum';
      }
    }

    if (!this.isPlottable(req)) return;

    this.loadingStart();
    try {
      const data: any = await sensor('register_graph', String(idSensor), req);
      if (this.mounted) {
        this.readingsLoad(data);
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
    this.hideSidebar();
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public loadFilter = async (): Promise<void> => {
    this.loadingStart();
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsSuper: boolean = (user.type === 'adm' || user.superuser === 1);
    try {
      const [typeSensorArray, companyArray] = await Promise.all([
        typeSensor<TypeSensorListDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        company<CompanyDO[]>('list') || [],
      ]);
      if (this.mounted) {
        if (userIsSuper) (companyArray as any[]).unshift({ id: 0, name: 'ALL' });
        this.setState({
          typeSensorArray,
          companyArray,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadVariables = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;

    if (idSensor) {
      this.loadingStart();
      try {
        const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
        const typeSensorId: number | undefined = sensorInfo?.typeSensor?.id;
        const typeVariableArray: TypeSensorVariableListDO[] = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId }) || [];

        if (this.mounted) {
          this.setState({
            typeVariableArray,
          });
        }
      } catch (error) {
        messageError('Error', getErrorMessage(error));
      }
      this.loadingFinish();
    }
  };

  public readingsLoad = (reqdata: any): void => {
    const { typeVariableArray, multiple } = this.state;

    let odd: boolean = true;
    const parsedData: any[] = reqdata.map((e: any) => {
      const serieData: any = e.data
        .map((point: any) => {
          const dateTimeStamp: any = point[0];
          const intervalTimeArray: any = [this.toTimestamp(dateTimeStamp) * 1000, point[1]];
          return intervalTimeArray;
        })
        .sort((a: any, b: any) => a[0] - b[0]);

      const regex: RegExp = /(\d+)/g;
      const typeVariable: any = typeVariableArray[parseInt(e.name.match(regex), 10)];

      const yAxis: number = odd ? 0 : 1;
      odd = multiple || !odd;

      return {
        type: 'line',
        yAxis,
        zIndex: 2,
        data: serieData,
        name: `${e.name} - ${typeVariable.variableName} [${typeVariable.variableUnit}]`,
      };
    });

    // Condition Pressure
    if (parsedData !== undefined) {
      for (let i: number = 0; i < parsedData.length; i++) {
        if (parsedData[i].name === 'D05 - Pressure [bars]') {
          parsedData[i].name = 'D05 - Pressure [hPa]';
          for (let j: number = 0; j < parsedData[i].data.length; j++) {
            parsedData[i].data[j][1] *= 1000;
          }
        }
      }
    }

    this.context?.updateParent({
      update: window.getStr(),
      multiple: this.state.multiple,
      graphInput: {
        data: parsedData,
      },
    });
  };

  public isPlottable = (data: any): boolean => {
    const { from, to, group, values } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    if (!group) {
      messageError('Error', 'Please select a time interval');
      return false;
    }
    if (!values || !values.length) {
      messageError('Error', 'Please select at least a variable to plot');
      return false;
    }
    return true;
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  /* Handlers */
  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type, checked } = (e as React.ChangeEvent<HTMLInputElement>).target;
    if (type === 'checkbox') {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  public handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const { companyId, typeSensorId } = this.state;

    const request: Record<string, any> = {};
    if (companyId) request.companyId = companyId;
    if (typeSensorId) request.typeSensorId = typeSensorId;
    request[name] = value;

    this.loadingStart();
    sensor<SensorListDO[]>('list', undefined, request)
      ?.then((resp: SensorListDO[]) => {
        this.setState((prevState: State) => ({
          ...prevState,
          [name]: +value,
          id: undefined,
          sensorArray: resp,
        }));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }), () => {
      this.loadVariables().catch(() => { /* pass */ });
    });
  };

  public handleDatePickerChanges = (config: any): void => {
    const hours: string[] = config.getHours();
    const date: Date[] = config.getDates();
    this.setState({
      date,
      timeIni: hours[0],
      timeEnd: hours[1],
      flatpickrMode: config.getMode(),
    });
    const preDataIni: Date = date[0];
    const newDataIni: string = moment(preDataIni).format('YYYY-MM-DD');
    this.setState({ dateIni: newDataIni });
    const preDataEnd: Date = date[1];
    if (this.state.flatpickrMode === 'range') {
      const newDataEnd: string = moment(preDataEnd).format('YYYY-MM-DD');
      this.setState({ dateEnd: newDataEnd });
    } else {
      const copyData: string = moment(newDataIni).add(1, 'days').format('YYYY-MM-DD');
      this.setState({ dateEnd: copyData });
    }
  };

  public handleChecked = (checked: boolean): void => {
    this.setState({
      checked,
    });
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setState({
      variables: selection,
    });
  };

  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public onAutoRefresh = (): boolean => {
    if (this.state.checked === true && this.state.autoRefresh === false) {
      this.setState({
        autoRefresh: true,
      });
      setTimeout(() => {
        if (this.state.checked === true && this.state.autoRefresh === true) {
          this.submit().catch(() => { /* pass */ });
        }
        this.setState({
          autoRefresh: false,
        });
      }, 60000);
    }

    return this.state.checked;
  };

  public render(): JSX.Element {
    const { companyArray, typeSensorArray, sensorArray, typeVariableArray } = this.state;
    const { date, flatpickrMode, groupDateTime } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');
    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        <div hidden={this.isToday() === false}>
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={this.onAutoRefresh()}
                onChange={this.handleChecked}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
        </div>
        { /* TODO: REVIEW !!!!! */ }
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="companyId"
              data={companyArray
                .filter((comp: CompanyDO): boolean => {
                  const a: boolean = userIsAdmin || !_.isEmpty(comp.name);
                  if (user.extra === undefined) {
                    companyArray.map((item: CompanyDO): DataSelect2 => {
                      return { title: item.name, text: item.name, id: item.id };
                    });
                  }
                  return a;
                })
                .map((item: CompanyDO): DataSelect2 => {
                  return {
                    id: item.id,
                    text: item.name,
                    title: item.name,
                  };
                })}
              value={this.state.companyId}
              options={{
                placeholder: 'Select Company',
              }}
              onChange={this.handleChangeFilter}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="typeSensorId"
              data={typeSensorArray.map((item: TypeSensorListDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.name}`,
                  title: `${item.name} [${item.code}]`,
                };
              })}
              value={this.state.typeSensorId}
              options={{
                placeholder: 'Select Sensor Type',
              }}
              onChange={this.handleChangeFilter}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              data={sensorArray.map((item: SensorListDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              value={this.state.id}
              options={{
                placeholder: 'Select Sensor To Show',
              }}
              onChange={this.handleChangeDevice}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              multiple
              name="variables"
              data={typeVariableArray.map((value: TypeSensorVariableListDO, idx: number): DataSelect2 => {
                return {
                  id: idx,
                  text: `${value.variableName}`,
                  title: `${value.variableName} [D${idx}] [${value.variableUnit}]`,
                };
              })}
              value={this.state.variables}
              options={{
                placeholder: 'Select Variables',
              }}
              onSelect={this.handleChangeMultiple}
              onUnselect={this.handleChangeMultiple}
              style={{ width: '99%' }}
              required
            />
          </div>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <label className="label">Multiple</label>
            <input
              className="inputNew"
              style={{ marginLeft: '20px', marginRight: '0px' }}
              type="checkbox"
              name="multiple"
              id="multiple"
              data-handler-type="boolean"
              checked={this.state.multiple || false}
              onChange={this.handleChange}
            />
          </div>
          <DatePicker
            icon
            hours={[]}
            dates={date}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleDatePickerChanges}
          />
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <div>
              <Select2
                name="groupTime"
                data={[
                  { id: 0, value: 0, text: 'Raw Data' },
                  { id: 1, value: 15, text: '15 Minutes' },
                  { id: 2, value: 30, text: '30 Minutes' },
                  { id: 3, value: 60, text: '1 Hour' },
                  { id: 4, value: 1440, text: 'Day' },
                  { id: 5, value: 43200, text: 'Month' },
                ]}
                value={this.state.groupTime}
                options={{
                  placeholder: 'Select Time Interval',
                }}
                onChange={this.handleChange}
                style={{ width: '99%' }}
                required
              />
            </div>
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={(): void => { this.submit().catch(() => { /* pass */ }); }}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
